import { Fragment, type ReactElement, type SVGProps } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLocale } from '../i18n/locale-context';
import { LOCALES, type Locale, setLocaleInStorage } from '../i18n/index';
import { toggleLocalePath } from '../i18n/locale-path';

/** Endonyms — each language named in its own language. Used as the accessible
 *  name (aria-label + visually-hidden text) since the switcher shows both flags
 *  at once regardless of the active UI locale. Exported — the landing header
 *  (`routes/landing/layout/landing-header.tsx`) reuses these SAME accessible
 *  names for its own (differently-behaved) switcher, so the two never drift. */
export const LOCALE_NAMES: Record<Locale, string> = {
  uz: "O'zbekcha",
  ru: 'Русский',
  kk: 'Қазақша',
  es: 'Español',
};

/** Inline SVG flags — crisp and reliable cross-platform (flag emoji don't render
 *  on many desktops). 20×14 rounded rect with a hairline edge so the light bands
 *  stay visible on the bone surface. Exported (via `LOCALE_FLAGS` below) so the
 *  landing header can reuse the exact same artwork instead of a copy-pasted 40
 *  lines of SVG. */
function UzFlag(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden {...props}>
      <defs>
        <clipPath id="uz-flag-clip">
          <rect width="20" height="14" rx="2.4" />
        </clipPath>
      </defs>
      <g clipPath="url(#uz-flag-clip)">
        <rect width="20" height="14" fill="#0099B5" />
        <rect y="4.4" width="20" height="5.2" fill="#fff" />
        <rect y="9.6" width="20" height="4.4" fill="#1EB53A" />
        <rect y="4.2" width="20" height="0.4" fill="#CE1126" />
        <rect y="9.4" width="20" height="0.4" fill="#CE1126" />
        {/* Crescent — a white disc carved by an offset blue disc. */}
        <circle cx="3.7" cy="2.2" r="1.35" fill="#fff" />
        <circle cx="4.45" cy="2.2" r="1.35" fill="#0099B5" />
      </g>
      <rect
        x="0.4"
        y="0.4"
        width="19.2"
        height="13.2"
        rx="2"
        fill="none"
        stroke="#000"
        strokeOpacity="0.1"
      />
    </svg>
  );
}

function RuFlag(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden {...props}>
      <defs>
        <clipPath id="ru-flag-clip">
          <rect width="20" height="14" rx="2.4" />
        </clipPath>
      </defs>
      <g clipPath="url(#ru-flag-clip)">
        <rect width="20" height="14" fill="#fff" />
        <rect y="4.667" width="20" height="4.667" fill="#0039A6" />
        <rect y="9.333" width="20" height="4.667" fill="#D52B1E" />
      </g>
      <rect
        x="0.4"
        y="0.4"
        width="19.2"
        height="13.2"
        rx="2"
        fill="none"
        stroke="#000"
        strokeOpacity="0.1"
      />
    </svg>
  );
}

function EsFlag(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden {...props}>
      <defs>
        <clipPath id="es-flag-clip">
          <rect width="20" height="14" rx="2.4" />
        </clipPath>
      </defs>
      <g clipPath="url(#es-flag-clip)">
        {/* Red–yellow–red, 1:2:1 (the coat of arms is dropped at 20×14). */}
        <rect width="20" height="14" fill="#AA151B" />
        <rect y="3.5" width="20" height="7" fill="#F1BF00" />
      </g>
      <rect
        x="0.4"
        y="0.4"
        width="19.2"
        height="13.2"
        rx="2"
        fill="none"
        stroke="#000"
        strokeOpacity="0.1"
      />
    </svg>
  );
}

function KzFlag(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden {...props}>
      <defs>
        <clipPath id="kz-flag-clip">
          <rect width="20" height="14" rx="2.4" />
        </clipPath>
      </defs>
      <g clipPath="url(#kz-flag-clip)">
        {/* Sky-blue field with the gold sun (simplified — the steppe eagle and
            hoist ornament are dropped at 20×14, they'd be an illegible smudge). */}
        <rect width="20" height="14" fill="#00AFCA" />
        <circle cx="10" cy="6.4" r="2.1" fill="#FEC50C" />
      </g>
      <rect
        x="0.4"
        y="0.4"
        width="19.2"
        height="13.2"
        rx="2"
        fill="none"
        stroke="#000"
        strokeOpacity="0.1"
      />
    </svg>
  );
}

export const LOCALE_FLAGS: Record<Locale, (props: SVGProps<SVGSVGElement>) => ReactElement> = {
  uz: UzFlag,
  ru: RuFlag,
  kk: KzFlag,
  es: EsFlag,
};

/**
 * m16 language pill-group. Swaps the old O'z / Ру text labels for inline SVG
 * flags (🇺🇿 / 🇷🇺). Each option keeps its full language name as an aria-label
 * and visually-hidden text; aria-pressed marks the active locale, and the active
 * flag stays at full opacity while inactive ones dim — so it's clear which is on.
 */
export function LanguageSwitcher() {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  function switchTo(l: Locale) {
    if (l === locale) return;
    setLocaleInStorage(l); // remember the preference
    const path = `${location.pathname}${location.search}${location.hash}`;
    void navigate(toggleLocalePath(path, l));
  }

  return (
    <div
      role="group"
      aria-label="Language switcher"
      className="flex items-center overflow-hidden rounded-sm border border-edge bg-bone"
    >
      {LOCALES.map((l, i) => {
        const Flag = LOCALE_FLAGS[l];
        const active = locale === l;
        return (
          <Fragment key={l}>
            {i > 0 && <span aria-hidden className="h-3.5 w-px shrink-0 bg-edge" />}
            <button
              onClick={() => switchTo(l)}
              aria-pressed={active}
              aria-label={LOCALE_NAMES[l]}
              title={LOCALE_NAMES[l]}
              className={`flex min-h-10 items-center px-3 py-2 transition-colors ${
                active ? 'bg-bone-surface' : 'hover:bg-bone-surface'
              }`}
            >
              <Flag
                className={`h-3.5 w-5 rounded-[3px] transition-opacity ${
                  active ? 'opacity-100' : 'opacity-55'
                }`}
              />
              <span className="sr-only">{LOCALE_NAMES[l]}</span>
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}
